
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

export interface EventImage {
  image_id: string;
  image_url: string;
  is_primary: boolean;
  display_order: number;
}

export const fetchEventImages = async (eventId: string): Promise<EventImage[]> => {
  try {
    const { data, error } = await supabase
      .from('event_images')
      .select('image_id, image_url, is_primary, display_order')
      .eq('event_id', eventId)
      .order('display_order', { ascending: true });

    if (error) throw error;
    
    return data as EventImage[];
  } catch (error) {
    console.error('Error fetching event images:', error);
    return [];
  }
};

export const uploadImageToStorage = async (file: File): Promise<string | null> => {
  try {
    if (!file.type.startsWith('image/')) {
      toast({
        title: "Invalid file type",
        description: "Please upload image files only",
        variant: "destructive",
      });
      return null;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "File too large",
        description: "Image must be less than 5MB",
        variant: "destructive",
      });
      return null;
    }

    const fileExt = file.name.split('.').pop();
    const fileName = `${crypto.randomUUID()}.${fileExt}`;
    const { error: uploadError } = await supabase.storage
      .from('event-images')
      .upload(fileName, file);

    if (uploadError) throw uploadError;

    const { data: { publicUrl } } = supabase.storage
      .from('event-images')
      .getPublicUrl(fileName);

    return publicUrl;
  } catch (error) {
    console.error('Error uploading image:', error);
    return null;
  }
};

export const saveImageToDatabase = async (
  eventId: string, 
  imageUrl: string, 
  isPrimary: boolean = false,
  displayOrder: number = 0
): Promise<EventImage | null> => {
  try {
    const { data, error } = await supabase
      .from('event_images')
      .insert({
        event_id: eventId,
        image_url: imageUrl,
        display_order: displayOrder,
        is_primary: isPrimary
      })
      .select();

    if (error) throw error;
    
    return data?.[0] as EventImage;
  } catch (error) {
    console.error('Error saving image to database:', error);
    return null;
  }
};

export const removeImageFromStorage = async (imageUrl: string): Promise<boolean> => {
  try {
    // Extract filename from URL
    const fileName = imageUrl.split('/').pop();
    if (!fileName) return false;

    // Remove from storage
    const { error } = await supabase.storage
      .from('event-images')
      .remove([fileName]);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error removing image from storage:', error);
    return false;
  }
};

export const removeImageFromDatabase = async (eventId: string, imageUrl: string): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('event_images')
      .delete()
      .eq('event_id', eventId)
      .eq('image_url', imageUrl);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error removing image from database:', error);
    return false;
  }
};

export const updatePrimaryImage = async (eventId: string, imageUrl: string): Promise<boolean> => {
  try {
    // First set all images to not primary
    const { error: resetError } = await supabase
      .from('event_images')
      .update({ is_primary: false })
      .eq('event_id', eventId);

    if (resetError) throw resetError;
    
    // Then set the selected image as primary
    const { error: updateError } = await supabase
      .from('event_images')
      .update({ is_primary: true })
      .eq('event_id', eventId)
      .eq('image_url', imageUrl);

    if (updateError) throw updateError;
    
    return true;
  } catch (error) {
    console.error('Error updating primary image:', error);
    return false;
  }
};
